import { useEffect, useState } from "react";
import { apiGet, apiPost } from "../lib/api";
import { Card, Label, Sub, Dot, relativeTime } from "../components/ui";

type Cell = {
  dataset: string;
  day: string;
  status: "ok" | "partial" | "missing" | "na" | "pending";
  covered: number;
  expected: number;
  missing: string[];
};
type Pulse = {
  coverage: {
    pct: number;
    byDatasetDay: Cell[];
    datasets: { key: string; label: string }[];
    from: string;
    to: string;
    today: string;
    area: string;
    areas: { id: string; name: string }[];
  };
  rowsToday: number;
  rejectedToday: number;
  freshness: { dataset: string; lastAt: string }[];
  mcpCalls7d: number;
  feed: { at: string; kind: string; title: string; detail: string; ok: boolean | null }[];
};

const CELL_CLASS: Record<Cell["status"], string> = {
  ok: "bg-green-600",
  partial: "bg-amber-600",
  missing: "bg-brand",
  na: "bg-neutral-100",
  pending: "bg-white border border-dashed border-neutral-300",
};

export default function PulseScreen() {
  const [data, setData] = useState<Pulse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ask, setAsk] = useState("");
  const [askResult, setAskResult] = useState<{ rows: Record<string, unknown>[]; error?: string } | null>(null);
  const [askBusy, setAskBusy] = useState(false);
  const [area, setArea] = useState<string>(() => {
    try { return localStorage.getItem("pulse.area") ?? ""; } catch { return ""; }
  });
  const [picked, setPicked] = useState<Cell | null>(null);

  useEffect(() => {
    setPicked(null);
    apiGet<Pulse>(`/api/admin/pulse${area ? `?area=${encodeURIComponent(area)}` : ""}`)
      .then(setData)
      .catch((e) => setError(String(e.message ?? e)));
  }, [area]);

  function chooseArea(v: string) {
    setArea(v);
    try { localStorage.setItem("pulse.area", v); } catch { /* per-viewer convenience only */ }
  }

  async function runAsk(e: React.FormEvent) {
    e.preventDefault();
    if (!ask.trim()) return;
    setAskBusy(true);
    setAskResult(null);
    try {
      const r = await apiPost<{ rows: Record<string, unknown>[] }>("/api/admin/explore/query", { sql: ask });
      setAskResult(r);
    } catch (e: any) {
      setAskResult({ rows: [], error: String(e.message ?? e) });
    } finally {
      setAskBusy(false);
    }
  }

  if (error) return <p className="text-sm text-brand">{error}</p>;
  if (!data) return <p className="text-sm text-neutral-400">Loading…</p>;

  const days: string[] = [];
  for (let d = data.coverage.from; d <= data.coverage.to; ) {
    days.push(d);
    const nd = new Date(`${d}T00:00:00Z`);
    nd.setUTCDate(nd.getUTCDate() + 1);
    d = nd.toISOString().slice(0, 10);
  }
  const byKey = new Map(data.coverage.byDatasetDay.map((c) => [`${c.dataset}|${c.day}`, c]));
  const labelOf = new Map(data.coverage.datasets.map((d) => [d.key, d.label]));
  const niceDay = (d: string) =>
    new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  const areaName = data.coverage.areas.find((a) => a.id === data.coverage.area)?.name ?? "whole estate";
  const freshest = data.freshness.reduce<{ dataset: string; lastAt: string } | null>(
    (a, b) => (!a || b.lastAt > a.lastAt ? b : a),
    null,
  );
  const stalest = data.freshness.reduce<{ dataset: string; lastAt: string } | null>(
    (a, b) => (!a || b.lastAt < a.lastAt ? b : a),
    null,
  );

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl text-ink">Pulse</h1>
      <form
        onSubmit={runAsk}
        className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 shadow-sm"
      >
        <span className="hidden shrink-0 rounded-md border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[11px] font-semibold text-neutral-500 sm:inline-block">
          SQL
        </span>
        <input
          value={ask}
          onChange={(e) => setAsk(e.target.value)}
          placeholder="Ask the data… SELECT ..."
          className="min-w-0 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-neutral-400 sm:text-sm"
        />
        <button
          type="submit"
          disabled={askBusy}
          className="min-h-[44px] shrink-0 px-1 text-sm font-semibold text-brand disabled:opacity-50"
        >
          Run
        </button>
      </form>

      {askResult && (
        <Card>
          {askResult.error ? (
            <p className="text-sm text-brand">{askResult.error}</p>
          ) : (
            <ResultTable rows={askResult.rows} />
          )}
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        <Card>
          <Label>Coverage · shown window</Label>
          <div className="mt-1.5 text-3xl font-semibold tracking-tight text-ink">{data.coverage.pct}%</div>
          <div className="mt-1 text-sm text-neutral-600">of due rows landed</div>
        </Card>
        <Card>
          <Label>Rows today</Label>
          <div className="mt-1.5 text-3xl font-semibold tracking-tight text-ink">
            {data.rowsToday.toLocaleString()}
          </div>
          <div className="mt-1 text-sm text-neutral-600">
            {data.rejectedToday > 0 ? <span className="text-brand">{data.rejectedToday} rejected</span> : "none rejected"}
          </div>
        </Card>
        <Card>
          <Label>Freshest · stalest</Label>
          <div className="mt-1.5 text-3xl font-semibold tracking-tight text-ink">
            {freshest ? relativeTime(freshest.lastAt) : "—"}
          </div>
          <div className="mt-1 text-sm text-neutral-600">
            {freshest ? `${freshest.dataset} · ` : ""}
            {stalest ? (
              <>
                {stalest.dataset} is <span className="text-amber-600">{relativeTime(stalest.lastAt)}</span> old
              </>
            ) : (
              "no writes yet"
            )}
          </div>
        </Card>
        <Card>
          <Label>MCP calls, 7 days</Label>
          <div className="mt-1.5 text-3xl font-semibold tracking-tight text-ink">{data.mcpCalls7d}</div>
        </Card>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3.5 lg:grid-cols-[1.5fr_1fr]">
        <Card>
          <div className="mb-3.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div>
              <h2 className="text-lg">Coverage · dataset × day</h2>
              <Sub>{days.length} days shown, incl. today and a few upcoming · tap a cell to see which sites</Sub>
            </div>
            <select
              value={data.coverage.area}
              onChange={(e) => chooseArea(e.target.value)}
              className="min-h-[44px] rounded-lg border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-brand"
              aria-label="Ops area"
            >
              {data.coverage.areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
              <option value="all">Whole estate</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <div
              className="grid items-center gap-1"
              style={{ gridTemplateColumns: `120px repeat(${days.length}, minmax(20px,1fr))`, minWidth: `${120 + days.length * 20}px` }}
            >
              <div className="sticky left-0 bg-white" />
              {days.map((d) => (
                <div key={d} className="text-center text-[10px] font-semibold text-neutral-400">
                  {new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { weekday: "narrow" })}
                </div>
              ))}
              {data.coverage.datasets.map((ds) => (
                <FragmentRow key={ds.key} label={ds.label}>
                  {days.map((d) => {
                    const cell = byKey.get(`${ds.key}|${d}`);
                    const status = cell?.status ?? "na";
                    const counted = cell && status !== "na" && status !== "pending";
                    const isPicked = picked?.dataset === ds.key && picked?.day === d;
                    return (
                      <button
                        type="button"
                        key={d}
                        onClick={() => setPicked(cell && isPicked ? null : cell ?? null)}
                        title={`${ds.label} · ${niceDay(d)} · ${status}${counted ? ` · ${cell.covered} of ${cell.expected} sites` : ""}`}
                        className={`h-[22px] rounded ${CELL_CLASS[status]} ${isPicked ? "ring-2 ring-ink ring-offset-1" : ""}`}
                      />
                    );
                  })}
                </FragmentRow>
              ))}
            </div>
          </div>
          {picked && (
            <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm">
              <div className="font-medium text-ink">
                {labelOf.get(picked.dataset)} · {niceDay(picked.day)} · {picked.status === "pending" ? "not yet due" : picked.status === "na" ? "not expected" : `${picked.covered} of ${picked.expected} sites in`}
              </div>
              {picked.missing.length > 0 && picked.status !== "na" && picked.status !== "pending" && (
                <div className="mt-1 text-neutral-600">
                  <span className="text-brand">Missing:</span> {picked.missing.join(", ")}
                  {picked.missing.length >= 25 ? "…" : ""}
                </div>
              )}
              {picked.missing.length === 0 && picked.status === "ok" && (
                <div className="mt-1 text-neutral-600">Every live site in {areaName} has a row.</div>
              )}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-neutral-600">
            <Legend color="bg-green-600" label="complete" />
            <Legend color="bg-amber-600" label="partial" />
            <Legend color="bg-brand" label="missing" />
            <Legend color="bg-neutral-100" label="not expected" />
            <Legend color="bg-white border border-dashed border-neutral-300" label="not yet due" />
          </div>
        </Card>
        <Card>
          <h2 className="mb-2 text-lg">Live feed</h2>
          <div className="flex flex-col">
            {data.feed.map((ev, i) => (
              <div key={i} className="grid grid-cols-[52px_10px_1fr] items-start gap-2.5 border-b border-neutral-100 py-2.5 last:border-0">
                <span className="pt-0.5 text-xs tabular-nums text-neutral-400">
                  {new Date(ev.at.includes("T") ? ev.at : `${ev.at.replace(" ", "T")}Z`).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <Dot tone={ev.ok === true ? "ok" : ev.ok === false ? "red" : "amber"} />
                <span className="text-[13.5px] text-ink">
                  {ev.title}
                  <small className="block text-xs text-neutral-400">{ev.detail}</small>
                </span>
              </div>
            ))}
            {data.feed.length === 0 && <p className="text-sm text-neutral-400">Nothing yet.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function FragmentRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div className="sticky left-0 bg-white pr-2 text-sm font-medium text-ink">{label}</div>
      {children}
    </>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <i className={`inline-block h-3 w-3 rounded-sm ${color}`} />
      {label}
    </span>
  );
}

function ResultTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows.length) return <p className="text-sm text-neutral-400">No rows.</p>;
  const cols = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c} className="border-b border-neutral-200 px-2.5 pb-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-400">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 200).map((r, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td key={c} className="border-b border-neutral-100 px-2.5 py-2 text-ink">
                  {String(r[c] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
